/**
 * NewObjectModal — collects the details for a new view / event / function /
 * procedure, then hands a generated CREATE skeleton to the parent (which opens
 * it in a seeded query tab). One modal, three shapes:
 *   kind 'view'  | 'event'   → just a name.
 *   kind 'function'          → Function vs Procedure, name, a parameters editor
 *                              (name · IN/OUT/INOUT · type), and a return type
 *                              (Function only).
 *
 * Props:
 *   open      bool
 *   kind      'view' | 'event' | 'function'
 *   database  string        — shown for context
 *   onCreate  (sql) => void — receives the generated skeleton (query-tab flow)
 *   onClose   () => void
 *   initialRoutineKind  'function' | 'procedure'  — preset the routine radio
 *   forEditor (bool)  — when set (routine flow), build the DROP+CREATE editor
 *                       skeleton and call onOpenInEditor instead of onCreate.
 *   onOpenInEditor  ({ database, kind, name, sql }) => void  — script-editor flow
 */
import { createSignal, createMemo, Show, For } from 'solid-js';
import { createStore } from 'solid-js/store';
import Modal from './Modal.jsx';
import { viewSkeleton, eventSkeleton, routineSkeleton, routineEditorSkeleton } from '../lib/skeletons.js';

const IDENT_RE = /^[A-Za-z_][\w$]*$/;
const TITLES = { view: 'New view', event: 'New event', function: 'New function / procedure' };

export default function NewObjectModal(props) {
  const [name, setName] = createSignal('');
  const [routineKind, setRoutineKind] = createSignal(props.initialRoutineKind || 'function'); // 'function' | 'procedure'
  const [returns, setReturns] = createSignal('INT');
  const [params, setParams] = createStore([]); // [{ name, mode, type }]

  const isFn = () => props.kind === 'function';
  const validName = () => IDENT_RE.test(name().trim());

  const addParam = () => setParams(params.length, { name: '', mode: 'IN', type: 'INT' });
  const removeParam = (i) => setParams((ps) => ps.filter((_, idx) => idx !== i));

  const sql = createMemo(() => {
    const nm = name().trim();
    if (props.kind === 'view') return viewSkeleton(nm);
    if (props.kind === 'event') return eventSkeleton(nm);
    const args = { kind: routineKind(), name: nm, params, returns: returns() };
    // forEditor → the DROP+CREATE (no-DELIMITER) shape the ScriptEditor saves.
    return props.forEditor ? routineEditorSkeleton(args) : routineSkeleton(args);
  });

  const create = () => {
    if (!validName()) return;
    if (props.forEditor) {
      props.onOpenInEditor?.({ database: props.database, kind: routineKind(), name: name().trim(), sql: sql() });
    } else {
      props.onCreate?.(sql());
    }
    props.onClose?.();
  };

  return (
    <Modal open={props.open} onClose={props.onClose}
           title={`${TITLES[props.kind] || 'New object'}${props.database ? ` · ${props.database}` : ''}`} wide={isFn()}>
      <div class="new-object">
        <Show when={isFn()}>
          <div class="new-object-row">
            <label class="new-object-label">Type</label>
            <div class="new-object-radios">
              <label class="checkbox"><input type="radio" name="rkind" checked={routineKind() === 'function'}
                     onChange={() => setRoutineKind('function')} /><span>Function</span></label>
              <label class="checkbox"><input type="radio" name="rkind" checked={routineKind() === 'procedure'}
                     onChange={() => setRoutineKind('procedure')} /><span>Stored procedure</span></label>
            </div>
          </div>
        </Show>

        <div class="new-object-row">
          <label class="new-object-label">Name</label>
          <input class="input" value={name()} placeholder="object_name" autofocus
                 onInput={(e) => setName(e.currentTarget.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && validName()) create(); }} />
          <Show when={name().trim() && !validName()}>
            <span class="new-object-err">Must start with a letter/underscore; letters, digits, _ only.</span>
          </Show>
        </div>

        <Show when={isFn()}>
          <div class="new-object-row">
            <label class="new-object-label">Parameters</label>
            <div class="new-object-params">
              <For each={params} fallback={<div class="muted" style="font-size:12px;">No parameters.</div>}>
                {(p, i) => (
                  <div class="new-object-param">
                    <Show when={routineKind() === 'procedure'} fallback={<span class="new-object-param-modeskip" />}>
                      <select class="input" value={p.mode} onChange={(e) => setParams(i(), 'mode', e.currentTarget.value)}>
                        <option value="IN">IN</option><option value="OUT">OUT</option><option value="INOUT">INOUT</option>
                      </select>
                    </Show>
                    <input class="input" placeholder="name" value={p.name}
                           onInput={(e) => setParams(i(), 'name', e.currentTarget.value)} />
                    <input class="input mono" placeholder="type (e.g. INT)" value={p.type}
                           onInput={(e) => setParams(i(), 'type', e.currentTarget.value)} />
                    <button class="btn btn-sm btn-ghost" title="Remove" onClick={() => removeParam(i())}>×</button>
                  </div>
                )}
              </For>
              <button class="btn btn-sm btn-ghost" onClick={addParam}>+ Add parameter</button>
            </div>
          </div>

          <Show when={routineKind() === 'function'}>
            <div class="new-object-row">
              <label class="new-object-label">Returns</label>
              <input class="input mono" value={returns()} placeholder="INT"
                     onInput={(e) => setReturns(e.currentTarget.value)} />
            </div>
          </Show>
        </Show>

        <div class="new-object-preview">
          <label class="new-object-label">Skeleton</label>
          <pre class="new-object-code mono">{sql()}</pre>
        </div>

        <div class="new-object-actions">
          <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={!validName()} onClick={create}>{props.forEditor ? 'Open in editor' : 'Open in query tab'}</button>
        </div>
      </div>
    </Modal>
  );
}
